import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { loadVideoGate, videoCardVisible } from '../lib/videoGate'
import { cardsFor, type DbNotice, type WelcomeCard } from '../lib/notices'
import { useHubLang } from '../lib/i18n'
import { shownIn, WELCOME, welcomeCardText } from '../lib/hubCopy'

/**
 * The welcome, as a modal. SHL-5 / FED-27, and the copy is content (091).
 *
 * Two of them exist: managers on 7 October, everybody on 13 October. Neither
 * is in this file, and that is the point — the wording is DRAFT with Chandra
 * and Lisa reviewing it, so it lives in `content_variant` and changes without
 * a deploy. What is here is the furniture.
 *
 * ── IT REUSES Dialog RATHER THAN GROWING A SECOND MODAL ─────────────────────
 *
 * `Dialog` already traps Tab, locks body scroll, handles Escape through the
 * surface stack, returns focus to whatever opened it, and — the reason it is
 * usable here at all — DOES NOT USE A PORTAL. `createPortal` throws under
 * `renderToString`, which would make the one surface ~930 people are guaranteed
 * to see the one surface the web suite cannot mount. Rule 11: a second modal
 * implementation is a second answer, and the two disagree about focus.
 *
 * ── SECTION CTAs, NOT COACH-MARKS (David, 18 Sep) ───────────────────────────
 *
 * Four cards, each with a button that GOES somewhere rather than pointing at
 * something. A coach-mark is coupled to the DOM it points at, and this hub has
 * two layouts: the drawer's bubble does not exist below 820px and the tab bar
 * does not exist above it, so pointing would need two sets of arrows and would
 * break the next time SHL-18 moves. A card that navigates has one behaviour at
 * every width and survives the layout changing under it.
 *
 * Still true of THIS modal. SHL-34 (David, 10 Oct) added the thing that does
 * point — a tour that starts after this closes — and it answers the objection
 * above rather than overruling it: each stop names candidates for both layouts
 * and is drawn centred when none is on screen. lib/tour.ts. Do not turn these
 * cards into coach-marks on the strength of the tour existing.
 *
 * ── THE TOUR IS THE WAY OUT, WHEN THE NOTICE OFFERS IT (180, 12 Oct) ───────
 *
 * David, 12 Oct: "I like the tour more than the 4 links." `tour` is a card key
 * like the others — comms chooses it per notice, and the 13 October welcome now
 * carries it alone — but it is NOT drawn in the grid. It is the footer's
 * primary button, beside "Have a look around": closing a welcome is the moment
 * the tour is for, and a lone card in a grid reads as one option among none.
 * Pressing it closes the modal (which records the dismissal, as every card
 * does) and starts the tour through AppShell's takeTour — on the hub at once,
 * from anywhere else on the hub after the navigation.
 *
 * ── THE BODY IS PROSE AND NOTHING LINKIFIES IT ──────────────────────────────
 *
 * Paragraphs split on blank lines, rendered as text. 057 is explicit that
 * nothing in this app turns free text into links, and a notice — written by
 * somebody else and shown to ~900 people — is the last place to start. The
 * only destinations here are the cards, and those come from a closed set.
 *
 * ── THE FALLBACK FLAG IS NOT DECORATION ─────────────────────────────────────
 *
 * `active_notices` resolves per row through `app.resolve_variant`, so a Spanish
 * speaker with no es-CL variant gets the en-CA welcome rather than an empty
 * screen (082's trap, which would have been silent on 13 October). The flag is
 * what stops them concluding the company simply publishes in English and never
 * filing the translation.
 */
export function WelcomeModal({
  notice,
  open,
  onClose,
  onNavigate,
  onOpenDrawer,
  onTakeTour,
}: {
  notice: DbNotice | null
  open: boolean
  /** Dismisses AND records it. The caller owns the write; this owns the screen. */
  onClose: () => void
  onNavigate: (href: string) => void
  /** Opens the Ask One panel. */
  onOpenDrawer: () => void
  /** 180. Starts the hub tour. Absent (the suite, a preview): the button only closes. */
  onTakeTour?: () => void
}) {
  const lang = useHubLang()
  const t = WELCOME[lang]
  /* 156 (11 Oct). While the hub's video is held back for the announcement,
     "Watch the announcement" would send people to a hub with nothing to
     watch — so that one card waits too. null = not read (yet, or the read
     failed): the card stays, because this modal is not what hides a video. */
  const wantsVideo = notice?.ctaKeys.includes('video') ?? false
  const [videoVisible, setVideoVisible] = useState<boolean | null>(null)
  useEffect(() => {
    if (!wantsVideo) return
    let live = true
    loadVideoGate()
      .then((g) => live && setVideoVisible(videoCardVisible(g)))
      .catch(() => live && setVideoVisible(null))
    return () => {
      live = false
    }
  }, [wantsVideo])
  if (!notice) return null

  const cards = cardsFor(notice.ctaKeys).filter((c) => c.key !== 'video' || videoVisible !== false)
  // 180: the tour is the footer's main button, never a card in the grid.
  const tour = cards.find((c) => c.kind === 'tour') ?? null
  const grid = cards.filter((c) => c.kind !== 'tour')
  const paragraphs = notice.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)

  function take(card: WelcomeCard) {
    // The modal closes on every card, and closing is what records the
    // dismissal. Leaving it open behind a navigation would put a scrim over
    // the thing the person just chose to go and look at.
    onClose()
    if (card.kind === 'navigate') onNavigate(card.route)
    else if (card.kind === 'drawer') onOpenDrawer()
    else onTakeTour?.()
  }

  return (
    <Dialog
      open={open}
      // The tag doubles as the modal's heading. `active_notices` returns the
      // variant TITLE under the name `tag` — named for its banner use, where it
      // is a short label. For a modal it is the headline, which is why the
      // seeded copy puts "Welcome to OneConetec.com" there rather than in the
      // body.
      title={notice.tag ?? t.fallbackTitle}
      onClose={onClose}
      className="welcome"
    >
      <div className="welcome__body">
        {notice.isFallback && (
          <p className="flag welcome__flag">{shownIn(lang, notice.variantLocale)}</p>
        )}
        {paragraphs.map((p, i) => (
          <p key={i} className="welcome__para">
            {p}
          </p>
        ))}
      </div>

      {grid.length > 0 && (
        <ul className="welcome__cards" aria-label={t.whereToStart}>
          {grid.map((card) => ({ card, c: welcomeCardText(lang, card) })).map(({ card, c }) => (
            <li key={card.key} className="welcome__card">
              <h3 className="welcome__cardtitle">{c.title}</h3>
              <p className="welcome__cardbody">{c.body}</p>
              {/* A BUTTON, not an anchor, even for the two that navigate.
                  Both have to close the modal first, and an anchor that is
                  cancelled by an onClick to do side effects is an anchor that
                  lies about middle-click. The strip's single CTA stays a real
                  anchor precisely because it has no side effect. */}
              <button
                type="button"
                className="btn btn--primary btn--sm welcome__cardbtn"
                onClick={() => take(card)}
              >
                {c.action}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="welcome__foot">
        <button type="button" className="btn btn--quiet" onClick={onClose}>
          {/* NOT "Dismiss" or "Close". This is the primary way out for most
              people and it should sound like finishing rather than refusing —
              the × in the header is already there for anybody who wants out. */}
          {t.lookAround}
        </button>
        {tour && (
          <button
            type="button"
            className="btn btn--primary welcome__tour"
            title={welcomeCardText(lang, tour).body}
            onClick={() => take(tour)}
          >
            {welcomeCardText(lang, tour).action}
          </button>
        )}
      </div>
    </Dialog>
  )
}
